import CommonData from 'app/src/data/CommonData';
import Decimal from 'app/src/data/base/Decimal';
import Localize from 'app/src/localize/Localize';
import PaymentTerminalModalViewController from 'app/src/controllers/payments/PaymentTerminalModalViewController';
import Terminal, { TerminalTransactionResponse } from 'app/src/payments/terminals/Terminal';
import PaymentAnalytics from 'app/src/payments/PaymentAnalytics';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import Payment from 'app/src/data/models/Payment';
import SalePayment from 'app/src/data/models/SalePayment';
import SaleCaluclationHelper from 'app/src/payments/SaleCalculationHelper';
import PaymentType from 'app/src/data/models/PaymentType';
import AppViewManager from 'app/AppViewManager';
import RetailManager from 'app/retail_manager/RetailManager';
import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import Format from 'app/src/data/Format';
import SignatureStatusCodes from 'app/src/payments/SignatureStatusCodes';
import $ from 'jquery';
import get from 'lodash/get';

import DepositRefundSelectionModal from 'payments/components/DepositRefundSelectionModal';
import { createReactRenderer } from 'helpers/react-renderer';

export default class PaymentOperations {
  /**
   * This method encapsulates the flow of performing an integrated swipe operation.  This includes
   * performing the payment and synchornizing the payment information with the server.
   *
   * @param {Decimal} amount     The amount to transact
   * @param {SaleManager} saleManager    The Sale Manager of the current Sale
   * @param {GatewayConfig} gateway    The gateway
   * @param {String} swipeData The encrypted data captured from the card reader
   */
  static doIntegratedSwipeOperation(amount, saleManager, gateway, swipeData) {
    const shopID = CommonData.selectedShop.id;
    const registerID = CommonData.selectedRegister.id;
    const saleID = saleManager.sale.id;
    let deferred = $.Deferred();

    if (shopID && registerID) {
      if (saleID && gateway.get('gatewayID')) {
        let payment = new Payment({
          method: Payment.Methods.BANK_CARD,
          totalAmount: amount,
          saleID,
          // TODO: Eventually the payment service will take care of setting the payment type
          // but for now just set it to the credit card type
          paymentTypeID: PaymentTypeManager.getPaymentIdForType(PaymentTypeManager.Types.SWIPE),
          shopID,
          registerID,
        });

        payment.gateway = gateway.get('gatewayID');
        payment.workflow = Payment.Workflow.SWIPER_PAYMENTS;
        payment.isManualEntryAllowed = false;
        payment.gatewayConfigID = gateway.id;
        payment.swipeData = swipeData;

        payment
          .save()
          .done(() => {
            saleManager
              .refreshSale()
              .done(() => {
                const status = payment.get('status');
                if (status !== Payment.Status.APPROVED) {
                  const errorMessage = get(payment.get('Transactions'), 'PaymentTransaction', [
                    { message: null },
                  ])[0].message;
                  deferred.reject(errorMessage);
                } else {
                  deferred.resolve();
                }
              })
              .fail(() => {
                deferred.reject();
              });
          })
          .fail(() => {
            const status = payment.get('status');
            if (status !== Payment.Status.APPROVED) {
              const errorMessage = get(payment.get('Transactions'), 'PaymentTransaction', [
                { message: null },
              ])[0].message;
              deferred.reject(errorMessage);
            }
          });
      } else {
        deferred.reject();
      }
    } else {
      deferred.reject();
    }

    return deferred.promise();
  }

  /**
   * This method encapsulates the flow of performing an integrated swipe operation.  This includes
   * performing the payment and synchornizing the payment information with the server.
   *
   * @param {Decimal} amount     The amount to transact
   * @param {SaleManager} saleManager    The Sale Manager of the current Sale
   * @param {GatewayConfig} gateway    The gateway
   * @param {Object} manualEntryData     Manual entry data
   */
  static doManualEntryTokenPayment(amount, saleManager, gateway, manualEntryData = {}) {
    const shopID = CommonData.selectedShop.id;
    const registerID = CommonData.selectedRegister.id;
    const saleID = saleManager.sale.id;
    const {
      postalCode,
      cardToken,
      expiryMonth,
      expiryYear,
      orderId,
      invoiceId,
      cardHolderName,
    } = manualEntryData;
    let deferred = $.Deferred();

    if (shopID && registerID) {
      if (saleID && gateway.get('gatewayID')) {
        let payment = new Payment({
          method: Payment.Methods.BANK_CARD,
          totalAmount: amount,
          saleID,
          // TODO: Eventually the payment service will take care of setting the payment type
          // but for now just set it to the credit card type
          paymentTypeID: PaymentTypeManager.getPaymentIdForType(PaymentTypeManager.Types.SWIPE),
          shopID,
          registerID,
        });

        payment.gateway = gateway.get('gatewayID');
        payment.workflow = Payment.Workflow.INTEGRATED_PAYMENTS;
        payment.gatewayConfigID = gateway.id;
        payment.cardToken = cardToken;
        payment.postalCode = postalCode;
        payment.cardHolderName = cardHolderName;
        payment.cardExpiryDate = `${expiryMonth}${expiryYear}`;
        payment.orderId = orderId;
        payment.invoiceId = invoiceId;

        payment
          .save()
          .done(response => {
            // Create a payment abstraction for our code to work with
            const integratedPayment = PaymentFactory.createPayment(
              PaymentTypeManager.Types.ECOM, // It's essentially an ECOM payment so, why not?
              new Decimal(amount),
              PaymentTypeManager.getPaymentIdForType(PaymentTypeManager.Types.SWIPE)
            );
            integratedPayment.model = new SalePayment(payment.get('SalePayment'), { parse: true });
            integratedPayment.serviceModel = payment;

            saleManager
              .refreshSale()
              .done(() => {
                deferred.resolve({
                  status: payment.get('status'),
                  response: get(response, 'Payment'),
                  payment: integratedPayment,
                });
              })
              .fail(() => {
                deferred.reject({ response });
              });
          })
          .fail(response => {
            deferred.reject({ response });
          });
      } else {
        deferred.reject();
      }
    } else {
      deferred.reject();
    }

    return deferred.promise();
  }

  /**
   * This method encapsulates the flow of performing a payment operation.  This includes interactions
   * with the terminal and synchronization of the payment information with the server.
   *
   * @param {Object} paymentOptions
   * @param {Decimal} paymentOptions.amount     The amount to transact with the terminal
   * @param {Terminal} paymentOptions.terminal  The terminal performing the transaction
   * @param {SaleManager} paymentOptions.saleManager    The Sale Manager of the current Sale
   * @param {GatewayConfig} paymentOptions.gatewayConfig    The gateway
   * @param {Boolean} paymentOptions.isManualEntry Optional - When true the terminal will prompt for manual
   *                                               card entry (when supported)
   * @param {Boolean} paymentOptions.isUnreferencedRefund Optional - when true the unreferenced refund flow will be
   *                                                      triggered (when supported)
   * @param {Payment} paymentOptions.recoveredPayment Optional - When specified, the system will attempt to
   *                                                  continue the specified payment. This can occurr if there
   *                                                  is a pending payment in the sale that was unresolved.
   */
  static doTerminalPaymentOperation(paymentOptions) {
    const { amount, terminal, saleManager, gatewayConfig, ...rest } = paymentOptions;

    let deferred = $.Deferred();

    // start the terminal payment
    terminal
      .processPayment({ amount, gatewayConfig, saleID: saleManager.sale.id, ...rest })
      .done(response => {
        // A terminal instance is expected to resolve with an approved status and
        // a payment.
        if (response && response.payment) {
          PaymentAnalytics.trackTerminalEvent(
            terminal,
            PaymentAnalytics.TerminalEventTypes.SALE,
            response.response,
            response.payment,
            gatewayConfig.get('gatewayID')
          );

          switch (response.response) {
            case Terminal.Response.APPROVED:
            case Terminal.Response.APPROVED_WITH_ERROR:
              // We are doing the following check to send a refund event
              // for transaction that requires card present (e.g.: Interac )
              // and uses doTerminalPaymentOperation instead of doVoidOperation
              PaymentAnalytics.trackPaymentOperationsEventGTM(
                amount instanceof Decimal && amount.isNegative()
                  ? PaymentAnalytics.TerminalEventTypes.REFUND
                  : PaymentAnalytics.TerminalEventTypes.SALE,
                PaymentAnalytics.TerminalEventStates.SUCCESS
              );

              // Add the payment to the sale and then return to the default payment view
              // when finished.
              saleManager
                .addPayment(response.payment, { ignoreValidationErrors: true })
                .done(() => {
                  deferred.resolve(response);
                })
                .fail(() => {
                  deferred.reject(response);
                });
              break;

            case Terminal.Response.PAYMENT_ALREADY_RECONCILED:
              // the payment has already been reconciled with the server so
              // just refresh the sale to get the latest information
              saleManager.refreshSale().always(() => {
                deferred.resolve(response);
              });
              break;

            default:
              PaymentAnalytics.trackPaymentOperationsEventGTM(
                PaymentAnalytics.TerminalEventTypes.SALE,
                PaymentAnalytics.TerminalEventStates.FAIL
              );

              window.console.error('Invalid response from terminal:', response);
              deferred.reject(response);
              break;
          }

          const signatureStatusCode =
            response.payment.serviceModel &&
            response.payment.serviceModel.paymentTransaction &&
            response.payment.serviceModel.paymentTransaction.has('parameters')
              ? response.payment.serviceModel.paymentTransaction.get('parameters')
                  .signatureStatusCode
              : null;

          if (signatureStatusCode && signatureStatusCode === SignatureStatusCodes.Codes.CANCELLED) {
            PaymentServiceAlert.warn(
              'No signature was recorded. ' +
                "If you wish to void the transaction, please click 'Remove'."
            );
          }
        } else {
          window.console.error('No payment supplied in response from terminal:', response);
          deferred.reject(response);
        }
      })
      .fail(response => {
        if (response) {
          const paymentAmount = get(response, 'payment.amount');

          PaymentAnalytics.trackTerminalEvent(
            terminal,
            PaymentAnalytics.TerminalEventTypes.SALE,
            response.response,
            response.payment,
            gatewayConfig.get('gatewayID')
          );

          // We are doing the following check to send a refund event
          // for transaction that requires card present (e.g.: Interac )
          // and uses doTerminalPaymentOperation instead of doVoidOperation
          PaymentAnalytics.trackPaymentOperationsEventGTM(
            amount instanceof Decimal && amount.isNegative()
              ? PaymentAnalytics.TerminalEventTypes.REFUND
              : PaymentAnalytics.TerminalEventTypes.SALE,
            PaymentAnalytics.TerminalEventStates.FAIL
          );

          let addPaymentDeferred = $.Deferred().resolve();
          if (response.payment) {
            // For terminal payments there's no functional difference between
            // updating or adding a payment.  When added (updated) the payment will
            // save the terminal response data (contained within the payment)
            addPaymentDeferred = saleManager.addPayment(response.payment, {
              ignoreValidationErrors: true,
            });
          }

          addPaymentDeferred.always(() => {
            if (response.payment) {
              const thePayment = saleManager.creditDebitPayments.findWhere({
                salePaymentID: get(response, 'payment.model.id'),
              });
              if (
                thePayment &&
                !thePayment.get('archived') &&
                !get(thePayment.get('CCCharge'), 'declined')
              ) {
                // The server is telling us that the payment was in fact approved so approve it
                response.response = Terminal.Response.APPROVED;
                deferred.resolve(response);
              } else {
                deferred.reject(response);
              }
            } else {
              deferred.reject(response);
            }
          });
        } else {
          window.console.error('Invalid response from terminal:', response);
          deferred.reject(response);
        }
      });

    return deferred.promise();
  }

  /**
   * This method call the abstract reset method.
   *
   * @param {Terminal} paymentOptions.terminal  The terminal performing the transaction
   * @error Throw error
   */
  static abandonTerminalPaymentOperation(paymentOptions) {
    try {
      const { terminal } = paymentOptions;
      terminal.reset();
    } catch (e) {
      throw e;
    }
  }

  /**
   * This method encapsulates the flow of performing a payment operation.  This includes displaying any
   * necessary UI, interactions with the terminal and synchronization of the payment information with
   * the server.
   *
   * @param {Object} paymentOptions
   * @param {Decimal} paymentOptions.amount     The amount to transact with the terminal
   * @param {Terminal} paymentOptions.terminal  The terminal performing the transaction
   * @param {SaleManager} paymentOptions.saleManager    The Sale Manager of the current Sale
   * @param {PaymentProvider} paymentOptions.paymentProvider The PaymentProvider for the current Shop
   * @param {GatewayConfig} paymentOptions.gatewayConfig    The gateway
   * @param {Boolean} paymentOptions.isManualEntry Optional - When true the terminal will prompt for manual
   *                                               card entry (when supported)
   * @param {Payment} paymentOptions.recoveredPayment Optional - When specified, the system will attempt to
   *                                                  continue the specified payment. This can occur if there
   *                                                  is a pending payment in the sale that was unresolved.
   */
  static doPaymentOperation(paymentOptions) {
    const { terminal, saleManager, paymentProvider, ...rest } = paymentOptions;
    let appViewManager = AppViewManager.getInstance();

    if (!terminal.usesProprietaryUI) {
      appViewManager.showModal(
        new PaymentTerminalModalViewController(terminal, {
          canCancel: paymentProvider.activeGatewaySupportsCancelFromPOS,
          saleID: saleManager.sale.id,
        })
      );
    }

    const paymentOperation = PaymentOperations.doTerminalPaymentOperation({
      terminal,
      saleManager,
      gatewayConfig: paymentProvider.activeGateway,
      ...rest,
    });

    paymentOperation.always(() => {
      if (!terminal.usesProprietaryUI) {
        appViewManager.dismissModal();
      }
    });

    return paymentOperation;
  }

  /**
   * This method encapsulates the flow of performing a refund/void operation.  It handles the display
   * of the UI, initiating the appropriate business logic and triggering Retail Manager if appropriate.
   *
   * @param {Payment} payment     The payment service payment to be voided
   * @param {SaleManager} saleManager     The Sale Manager of the current Sale
   * @param {PaymentProvider} paymentProvider     The Payment Provider of the current Shop
   */
  static doVoidOperation(payment, saleManager, paymentProvider) {
    let deferred = $.Deferred();
    // TODO: Once the terminals and SaleManager use the serviceModel in the payments then we don't need
    // to look for the service payment in the sale manager.
    payment.serviceModel =
      saleManager.getServicePayment(payment.model.get('paymentID')) || payment.serviceModel;

    if (payment.serviceModel) {
      // For terminals that are voided using terminal (or their abstraction)
      if (!paymentProvider.activeGatewaySupportsIntegratedRefunds) {
        if (RetailManager.active) {
          RetailManager.cardReader.initiatePaymentTerminalRefund(
            saleManager.sale.id.toString(),
            payment.serviceModel.paymentTransaction.get('gatewayRecordRef'),
            payment.amount.toString(),
            saleManager.pendingBalance.toString(),
            payment.serviceModel.id
          );
          deferred.resolve(payment);
        } else {
          PaymentOperations.selectTerminalForOperation(paymentProvider)
            .done(terminal => {
              let appViewManager = AppViewManager.getInstance();
              appViewManager.showModal(
                new PaymentTerminalModalViewController(terminal, {
                  message: Localize.translate('Waiting for authorization...'),
                  canCancel: paymentProvider.activeGatewaySupportsCancelFromPOS,
                })
              );

              terminal
                .voidTransaction(payment, payment.serviceModel)
                .done(() => {
                  PaymentAnalytics.trackTerminalEvent(
                    terminal,
                    PaymentAnalytics.TerminalEventTypes.VOID,
                    Terminal.Response.APPROVED,
                    payment,
                    paymentProvider.activeGatewayProcessorType
                  );

                  PaymentAnalytics.trackPaymentOperationsEventGTM(
                    PaymentAnalytics.TerminalEventTypes.VOID,
                    PaymentAnalytics.TerminalEventStates.SUCCESS
                  );
                  // update the payment transaction details
                  payment
                    .commit()
                    .done(() => {
                      saleManager
                        .refreshSale()
                        .done(() => {
                          PaymentOperations.adjustDepositRefunds(saleManager)
                            .done(() => {
                              deferred.resolve(payment);
                            })
                            .fail(() => {
                              deferred.reject();
                            });
                        })
                        .fail(() => {
                          deferred.reject();
                        });
                    })
                    .fail(() => {
                      deferred.reject();
                    });
                })
                .fail(error => {
                  PaymentAnalytics.trackTerminalEvent(
                    terminal,
                    PaymentAnalytics.TerminalEventTypes.VOID,
                    error,
                    payment,
                    paymentProvider.activeGatewayProcessorType
                  );

                  PaymentAnalytics.trackPaymentOperationsEventGTM(
                    PaymentAnalytics.TerminalEventTypes.VOID,
                    PaymentAnalytics.TerminalEventStates.FAIL
                  );

                  // update the payment transaction details
                  payment.commit();

                  deferred.reject();
                })
                .always(() => {
                  appViewManager.dismissModal();
                });
            })
            .fail(error => {
              deferred.reject(error);
            });
        }
      } else {
        // For terminals that are refunded through the Payment Service
        showNotifier('working');
        saleManager
          .removePayment(payment)
          .done(() => {
            PaymentAnalytics.trackPaymentOperationsEventGTM(
              PaymentAnalytics.TerminalEventTypes.VOID,
              PaymentAnalytics.TerminalEventStates.SUCCESS
            );
            deferred.resolve(payment);
          })
          .fail(() => {
            PaymentAnalytics.trackPaymentOperationsEventGTM(
              PaymentAnalytics.TerminalEventTypes.VOID,
              PaymentAnalytics.TerminalEventStates.FAIL
            );
            deferred.reject();
          })
          .always(() => {
            hideNotifier('working');
          });
      }
    } else {
      deferred.reject();
      window.console.error("The payment does not have a service payment.'");
    }

    return deferred.promise();
  }

  /**
   * This method encapsulates the flow of performing a depost refund operation.  It takes care of presenting any
   * necessary UI and selecting the appropriate method of taking a deposit refund depending on the current
   * gateway.
   *
   * @param {DepositPayment} payment     The deposit payment to be refunded
   * @param {SaleManager} saleManager     The Sale Manager of the current Sale
   * @param {PaymentProvider} paymentProvider     The Payment Provider of the current Shop
   */
  static doRefundDepositOperation(deposit, saleManager, paymentProvider) {
    let deferred = $.Deferred();

    saleManager
      .commitPaymentToServer(deposit)
      .done(() => {
        let refundPaymentDeferred = $.Deferred();
        if (paymentProvider.activeGatewaySupportsIntegratedRefunds) {
          refundPaymentDeferred = saleManager.deposits.refund(deposit);
        } else {
          let amount = Format.formatMoney(deposit._model.get('amount').negated());
          PaymentOperations.selectTerminalForOperation(paymentProvider, amount)
            .done(terminal => {
              PaymentOperations.doPaymentOperation({
                amount: deposit.depositPayment.amount,
                terminal,
                saleManager,
                paymentProvider,
              })
                .done(() => {
                  refundPaymentDeferred.resolve();
                })
                .fail(() => {
                  refundPaymentDeferred.reject();
                });
            })
            .fail(error => {
              refundPaymentDeferred.reject(error);
            });
        }

        refundPaymentDeferred
          .fail(error => {
            // the deposit payment failed so archive the balancing credit account payment
            deposit.model.set('archived', true);
            saleManager.commitPaymentToServer(deposit);

            deferred.reject(error);
          })
          .always(() => {
            saleManager
              .refreshSale()
              .done(() => {
                deferred.resolve();
              })
              .fail(() => {
                deferred.reject();
              });
          });
      })
      .fail(() => {
        deferred.reject();
      });

    deferred
      .done(() => {
        flashNotifier(
          'success',
          Localize.translate(
            `Deposit Refund Of <var>${Format.formatMoney(deposit.amount)}</var> Added.`
          ),
          4
        );
      })
      .fail(error => {
        const displayableError =
          error instanceof TerminalTransactionResponse ? error.response : error;
        alertUser(Localize.translate(displayableError || 'Could not add the refund.'));
      });

    return deferred.promise();
  }

  /**
   * Selects the terminal for an operation.  This would be used for any operation that requires terminal
   * interaction.
   *
   * @param {PaymentProvider} paymentProvider The payment provider
   */
  static selectTerminalForOperation(paymentProvider, amount = null) {
    let deferred = $.Deferred();

    if (paymentProvider.terminals.length === 1) {
      // Only one?  Just select it without prompting
      deferred.resolve(paymentProvider.terminals[0]);
    } else if (paymentProvider.terminals.length) {
      // More than one?  Let's provide a modal for the user to select the one they want
      let terminal = paymentProvider.getTerminal(CommonData.selectedRegister.get('ccTerminalID'));
      if (!this.selectTerminalModal) {
        const RenderedComponent = createReactRenderer({
          component: DepositRefundSelectionModal,
          selector: '[data-react-root="ReactModal"]',
          props: {
            onRefund: terminalID => {
              terminal = paymentProvider.getTerminal(terminalID);
              deferred.resolve(terminal);
            },
            onCancel: () => {
              delete this.selectTerminalModal;
              RenderedComponent.unmount();
              deferred.reject();
            },
            lastUsedTerminal: terminal,
            terminals: paymentProvider.terminals,
            amount,
          },
        });
        this.selectTerminalModal = RenderedComponent.mount();
      }
    } else {
      deferred.reject(Localize.translate('No terminals are configured.'));
    }

    return deferred.promise();
  }

  /**
   * Adjusts the credit account amount in the event that there's insufficient balancing payments
   * in the sale to cover the deposit refunds.  This can occur, for instance, when a terminal
   * deposit refund payment is voided, for instance.
   *
   * @param {SaleManager} saleManager The sale manager
   */
  static adjustDepositRefunds(saleManager) {
    let deferred = $.Deferred();
    // Make sure that the balancing credit account payments do not exceed
    // our current refunds.
    if (saleManager && saleManager.deposits && !saleManager.deposits.refundTotal.isZero()) {
      const totalRefundPayments = SaleCaluclationHelper.totalRefundPaymentsInSale(saleManager);
      const totalDepositRefunds = saleManager.deposits.refundTotal;
      if (totalDepositRefunds.isGreaterThan(totalRefundPayments)) {
        const creditAccountPayments = saleManager.getPaymentsOfType(
          PaymentType.Types.CREDIT_ACCOUNT
        );
        const depositDifference = totalDepositRefunds.minus(totalRefundPayments);

        // let's try to find the credit account payment that we need to archive
        const salePayment = creditAccountPayments.find(creditAccountPayment =>
          creditAccountPayment.get('amount').isEqualTo(depositDifference)
        );

        if (salePayment) {
          let creditAccountPayment = PaymentFactory.createPaymentFromModel(salePayment);
          creditAccountPayment.model.set({
            archived: true,
          });

          saleManager
            .commitPaymentToServer(creditAccountPayment)
            .done(() => {
              deferred.resolve();
            })
            .fail(() => {
              deferred.reject();
            });
        } else {
          deferred.resolve();
        }
      } else {
        deferred.resolve();
      }
    }

    return deferred.promise();
  }

  /**
   * Allows Merchant to cancel the flow while waiting for input from the user.
   *
   * @param {Object} paymentOptions
   * @param {Terminal} paymentOptions.terminal  The terminal performing the transaction
   * @param {Payment} paymentOptions.payment  The payment currently in process
   */
  static cancelPayment(paymentOptions) {
    const { terminal, payment } = paymentOptions;
    return terminal.cancelPayment(payment);
  }
}
